/**
 * The scrolling view (docs/spec/04 §4): it starts to scroll when Digby comes within a few cells of
 * its edge and keeps going until he is centred, at a steady pixel speed, clamped to the cave.
 */

import { CELL } from './sprites/types';

const MARGIN_X = 4;
const MARGIN_Y = 3;

class Axis {
  pos = 0;
  private scrolling = false;

  /** The leftmost/topmost allowed position; a cave smaller than the view is centred. */
  private clamp(p: number, view: number, extent: number): number {
    if (extent <= view) return -Math.floor((view - extent) / 2);
    return Math.min(Math.max(0, p), extent - view);
  }

  snap(target: number, view: number, extent: number): void {
    this.pos = this.clamp(Math.round(target + CELL / 2 - view / 2), view, extent);
    this.scrolling = false;
  }

  follow(target: number, view: number, extent: number, margin: number, pxPerMs: number, dtMs: number): void {
    const local = target - this.pos;
    if (!this.scrolling && (local < margin * CELL || local > view - (margin + 1) * CELL)) this.scrolling = true;
    const goal = this.clamp(Math.round(target + CELL / 2 - view / 2), view, extent);
    if (!this.scrolling) {
      this.pos = this.clamp(this.pos, view, extent);
      return;
    }
    const stepPx = pxPerMs * dtMs;
    if (Math.abs(goal - this.pos) <= stepPx) {
      this.pos = goal;
      this.scrolling = false;
    } else this.pos += Math.sign(goal - this.pos) * stepPx;
  }
}

export class Camera {
  private readonly ax = new Axis();
  private readonly ay = new Axis();

  /** Top-left of the view in cave pixels, rounded for crisp drawing. */
  get x(): number {
    return Math.round(this.ax.pos);
  }

  get y(): number {
    return Math.round(this.ay.pos);
  }

  /** Centre on (px, py) at once: the start of a cave. */
  snap(px: number, py: number, viewW: number, viewH: number, caveW: number, caveH: number): void {
    this.ax.snap(px, viewW, caveW * CELL);
    this.ay.snap(py, viewH, caveH * CELL);
  }

  /** Follow Digby's pixel position; the scroll runs at one and a half cells per tick. */
  follow(px: number, py: number, viewW: number, viewH: number, caveW: number, caveH: number, tickMs: number, dtMs: number): void {
    const speed = (CELL * 1.5) / Math.max(1, tickMs);
    this.ax.follow(px, viewW, caveW * CELL, MARGIN_X, speed, dtMs);
    this.ay.follow(py, viewH, caveH * CELL, MARGIN_Y, speed, dtMs);
  }
}
